import type { Company, Job, Deal, AuthUser } from "../../../api/types";
import { ImportTableColumn } from "../../../utilities/types";
import { FIELD_TO_PROPERTY_MAP, ValidationContext, isFieldVisible } from "../../../utilities/jobValidation";
import { MRT_Row } from "material-react-table";
import { MergeConflict, MergeConflictOption, MergeResolutions } from "./ImportTable.types";

/**
 * Get column visibility based on company settings and user context
 */
export const getColumnVisibilityFromCompanySettings = (
	company?: Company,
	user?: AuthUser,
	deal?: Deal,
): Record<string, boolean> => {
	const visibility: Record<string, boolean> = {};

	// Create validation context for visibility checks
	const context: ValidationContext = {
		company,
		user,
		deal,
	};

	// Use centralized isFieldVisible for all fields
	Object.keys(FIELD_TO_PROPERTY_MAP).forEach((key) => {
		const fieldType = key as ImportTableColumn;
		visibility[fieldType] = isFieldVisible(fieldType, context);
	});

	return visibility;
};

/**
 * Check if all jobs have no attached files
 */
export function allJobsHaveNoFiles(jobs: Job[]) {
	for (const job of jobs) {
		if (job.files.boundaryGeoJSON?.features?.length) return false;
		if (job.files.zonesGeoJSON?.features?.length) return false;
		if (job.files.pointsGeoJSON?.features?.length) return false;
	}
	return true;
}

/**
 * Find a deal that matches the specified parameters
 */
export function findDeal(deals: Deal[], density: number, depth: number, labPackage: string, micros: number | string) {
	return deals.find((deal: Deal) => {
		const dealLabPackage = deal.testPackage;
		micros = parseFloat(micros.toString());
		return (
			deal.density == density &&
			deal.depth == depth &&
			deal.frequencyOfMicros == micros &&
			(dealLabPackage == labPackage ||
				((labPackage == "" || labPackage == "No test package specified") &&
					(dealLabPackage == "" || dealLabPackage == undefined)))
		);
	});
}

/**
 * Helper function to update all jobs in a group and their subRow caches
 * This ensures consistent group update behavior across all column types
 *
 * @param row - The MRT row representing the group
 * @param jobs - The current jobs array
 * @param updateFn - Function to update each job
 * @returns Updated jobs array
 */
export function updateJobGroup(row: MRT_Row<Job>, jobs: Job[], updateFn: (job: Job) => void): Job[] {
	const newJobs = [...jobs];

	// Update all jobs in the group using subRows
	if (row.subRows) {
		for (const subRow of row.subRows) {
			// Find the job in our jobs array that corresponds to this subRow
			const job = newJobs.find((j) => j.id === subRow.original.id);
			if (job) {
				updateFn(job);
			}
		}
	}

	return newJobs;
}

/**
 * Update subrows in a deal-based group when a field changes
 */
export function dealBasedSubRowUpdate(row: any, field: keyof Job, newValue: any, allDeals: Deal[]) {
	if (row.subRows) {
		for (const subRow of row.subRows) {
			const job = subRow._valuesCache as Job;
			if (subRow.original.importGrowerName === row.original.importGrowerName) {
				subRow._valuesCache[field] = newValue;
				subRow._valuesCache.dealIds = [
					(
						findDeal(
							allDeals,
							job.importDensity ?? 0,
							job.importDepth ?? 0,
							job.externalTestPackageId ?? "",
							job.externalFrequencyOfMicros ?? 0,
						)?.id || "custom"
					).toString(),
				];
			}
		}
	}
}

// ============= MERGE CONFLICT DETECTION =============

/**
 * Properties that should be merged when combining jobs.
 * Excludes: id (unique identifier), files (handled separately), and system-generated fields.
 */
const MERGEABLE_PROPERTIES: (keyof Job)[] = [
	"importGrowerName",
	"importFarmName",
	"importFieldName",
	"importDensity",
	"importDepth",
	"externalTestPackageId",
	"externalFrequencyOfMicros",
	"isReadyToSample",
	"isTestJob",
	"fieldPriority",
	"salespersonIds",
	"branchIds",
	"lastCrop",
	"nextCrop",
	"yieldGoal",
	"dateFieldReadyEstimated",
	"labSubmissionCode",
	"labInstructions",
	"growerPhoneNumber",
	"submissionNotes",
	"submitterEmail",
	"submitterName",
	"eventId",
	"dealIds",
	"microsList",
	"collectBoundary",
	"importLabName",
	"externalLabId",
	"externalId",
	"externalFarmId",
];

/**
 * Human-readable display names for job properties
 */
const PROPERTY_DISPLAY_NAMES: Partial<Record<keyof Job, string>> = {
	importGrowerName: "Grower",
	importFarmName: "Farm",
	importFieldName: "Field",
	importDensity: "Density",
	importDepth: "Depth",
	externalTestPackageId: "Test Package",
	externalFrequencyOfMicros: "Micros Frequency",
	isReadyToSample: "Ready to Sample",
	isTestJob: "Test Job",
	fieldPriority: "Priority",
	salespersonIds: "Salesperson",
	branchIds: "Branch",
	lastCrop: "Last Crop",
	nextCrop: "Next Crop",
	yieldGoal: "Yield Goal",
	dateFieldReadyEstimated: "Est. Field Ready Date",
	labSubmissionCode: "Lab Submission Code",
	labInstructions: "Lab Instructions",
	growerPhoneNumber: "Grower Phone",
	submissionNotes: "Submission Notes",
	submitterEmail: "Submitter Email",
	submitterName: "Submitter Name",
	eventId: "Event ID",
	dealIds: "Deal",
	microsList: "Micros List",
	collectBoundary: "Collect Boundary",
	importLabName: "Lab Name",
	externalLabId: "External Lab ID",
	externalId: "External ID",
	externalFarmId: "External Farm ID",
};

/**
 * Check if a value is considered "truthy" for merge purposes.
 * Empty strings, 0, false, null, undefined, and empty arrays are falsy.
 */
function isTruthyForMerge(value: unknown): boolean {
	if (value === null || value === undefined) return false;
	if (value === "") return false;
	if (value === 0) return false;
	if (value === false) return false;
	if (Array.isArray(value) && value.length === 0) return false;
	return true;
}

/**
 * Check if two values are equal for merge conflict detection.
 */
function areValuesEqual(a: unknown, b: unknown): boolean {
	// Handle arrays
	if (Array.isArray(a) && Array.isArray(b)) {
		if (a.length !== b.length) return false;
		return a.every((val, idx) => areValuesEqual(val, b[idx]));
	}

	// Handle dates
	if (a instanceof Date && b instanceof Date) {
		return a.getTime() === b.getTime();
	}

	// Handle objects (shallow comparison)
	if (typeof a === "object" && typeof b === "object" && a !== null && b !== null) {
		return JSON.stringify(a) === JSON.stringify(b);
	}

	return a === b;
}

/**
 * Format a value for display in the conflict resolution modal.
 */
export function formatValueForDisplay(value: unknown): string {
	if (value === null || value === undefined) return "(empty)";
	if (value === "") return "(empty)";
	if (value === true) return "Yes";
	if (value === false) return "No";
	if (value instanceof Date) return value.toLocaleDateString();
	if (Array.isArray(value)) {
		if (value.length === 0) return "(empty)";
		return value.join(", ");
	}
	return String(value);
}

/**
 * Create a label for a job in the conflict resolution UI.
 */
function getJobLabel(job: Job, index: number): string {
	const fieldName = job.importFieldName || job.fieldName || `Job ${index + 1}`;
	return fieldName;
}

/**
 * Detect merge conflicts between multiple jobs.
 * Returns an array of conflicts where multiple jobs have different truthy values.
 */
export function detectMergeConflicts(jobs: Job[]): MergeConflict[] {
	const conflicts: MergeConflict[] = [];

	for (const propKey of MERGEABLE_PROPERTIES) {
		const truthyValues: MergeConflictOption[] = [];

		for (let i = 0; i < jobs.length; i++) {
			const job = jobs[i];
			const value = job[propKey];

			if (isTruthyForMerge(value)) {
				// Check if this value is already in our truthy values (deduplication)
				const existingOption = truthyValues.find((opt) => areValuesEqual(opt.value, value));
				if (!existingOption) {
					truthyValues.push({
						value,
						jobIndex: i,
						jobLabel: getJobLabel(job, i),
					});
				}
			}
		}

		// If there are 2+ different truthy values, we have a conflict
		if (truthyValues.length >= 2) {
			conflicts.push({
				propertyKey: propKey,
				displayName: PROPERTY_DISPLAY_NAMES[propKey] || propKey,
				options: truthyValues,
			});
		}
	}

	return conflicts;
}

/**
 * Compute the merged value for a property using truthy-wins logic.
 * If resolutions are provided and this property has a resolution, use that.
 * Otherwise, find the first truthy value among the jobs.
 */
export function getMergedValue(jobs: Job[], propKey: keyof Job, resolutions?: MergeResolutions): unknown {
	// Check if we have a resolution for this property
	if (resolutions && propKey in resolutions) {
		return resolutions[propKey];
	}

	// Find the first truthy value
	for (const job of jobs) {
		const value = job[propKey];
		if (isTruthyForMerge(value)) {
			return value;
		}
	}

	// No truthy value found, return the first job's value
	return jobs[0]?.[propKey];
}

/**
 * Get the list of mergeable properties.
 */
export function getMergeableProperties(): (keyof Job)[] {
	return [...MERGEABLE_PROPERTIES];
}
